<template>
  <div>
    <!-- Vehicle Information Section -->
      <b-card 
    class="mb-3 shadow-sm" 
    :class="{ 'd-none': isCollapsed }"
    body-class="p-3"
  >
      <h3 class="font-weight-semibold mb-3 text-primary">
        {{ vehicle?.license_plate || $t('webviewCall.noVehicle') }}
      </h3>
      
      <b-row class="mb-1 small">
        <b-col cols="12" class="d-flex align-items-center">
          <i class="mdi mdi-cellphone-basic mr-2" style="width: 2rem; font-size: 1.2rem; text-align: center;"></i>
          <h6 class="mb-0">{{ vehicle?.imei || '-' }}</h6>
        </b-col>
      </b-row>
            
      <b-row class="mb-1 small">
        <b-col cols="12" class="d-flex align-items-center">
          <i class="mdi mdi-phone mr-2" style="width: 2rem; font-size: 1.2rem; text-align: center;"></i>
          <h6 class="mb-0">{{ vehicle?.sim || '-' }}</h6>
        </b-col>
      </b-row>

      <b-row class="mb-1 small">
        <b-col cols="12" class="d-flex align-items-center">
          <i class="mdi mdi-car mr-2" style="width: 2rem; font-size: 1.2rem; text-align: center;"></i>
          <h6 class="mb-0">{{ vehicleType }}</h6>
        </b-col>
      </b-row>

      <b-row class="mb-0 small">
        <b-col cols="12" class="d-flex align-items-center">
          <i class="mdi mdi-account mr-2" style="width: 2rem; font-size: 1.2rem; text-align: center;"></i>
          <h6 class="mb-0">{{ driverName }}</h6>
        </b-col>
      </b-row>
    </b-card>

    <!-- Call Status Section -->
    <div class="mb-3 text-center">
      <h6 class="mb-0" :class="statusColorClass">
        {{ statusMessage }}
      </h6>
    </div>

    <!-- Call Control Buttons -->
    <div class="d-flex">
      <b-button
        variant="outline-primary"
        @click="handleToggleMute"
        class="me-3"
      >
        <i :class="isMicMuted ? 'mdi mdi-microphone-off' : 'mdi mdi-microphone'" style="font-size: 1.25rem;"></i>
      </b-button>
      
      <b-button
        :variant="callState === 'active' ? 'danger' : 'success'"
        class="d-flex w-100 text-center align-items-center justify-content-center"
        @click="handleStartCallDisconnect"
      >
        <i class="mdi mdi-phone" style="font-size: 1.25rem;"></i>
        <p class="mb-0 ms-2">{{ callState === 'active' ? $t('webviewCall.endCall') : $t('webviewCall.startCall')  }}</p>
      </b-button>
    </div>
  </div>
</template>

<script>
import { Helper } from '@/mixin/helper';
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'WebviewCallCard',
  
  mixins: [Helper],
  
  props: {
    vehicle: {
      type: Object,
    },
    callState: {
      type: String,
      required: true,
      default: 'idle',
      validator: (value) => {
        return ['idle', 'connecting', 'active', 'failed'].includes(value)
      }
    },
    isCollapsed: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    ...mapGetters('video', ['fleetsListFilter', 'fleetsList']),
    
    driverName() {
      return this.vehicle?.driver1_name || this.$t('webviewCall.noDriver')
    },

    vehicleType() {
      const deviceCode = this.vehicle?.device_code
      if (!deviceCode) return this.$t('webviewCall.unknown')
      
      // Map device codes to vehicle types
      const typeMap = {
        'car': this.$t('webviewCall.types.car'),
        'truck': this.$t('webviewCall.types.truck'),
        'motorcycle': this.$t('webviewCall.types.motorcycle'),
        'bus': this.$t('webviewCall.types.bus')
      }
      
      return typeMap[deviceCode] || this.$t('webviewCall.types.car')
    },

    statusMessage() {
      const messages = {
        'idle': this.$t('webviewCall.status.idle'),
        'connecting': this.$t('webviewCall.status.connecting'),
        'active': this.$t('webviewCall.status.active'),
        'failed': this.$t('webviewCall.status.failed')
      }
      
      return messages[this.callState] || this.$t('webviewCall.status.idle')
    },

    statusColorClass() {
      const colorMap = {
        'connecting': 'text-warning',
        'active': 'text-success',
        'failed': 'text-danger',
        'idle': 'text-muted'
      }
      
      return colorMap[this.callState] || 'text-muted'
    },

    isMicMuted() {
      return this.$store.state.voip?.isMuted || false
    }
  },

  methods: {
    ...mapActions('video', ['getTotalCamera', 'setVideoStreamStatus', 'vehicleStatusByVehicleId',]),

    startCall() {
      const deviceType = this.getDeviceType(this.vehicle.device_code)
      this.$store.dispatch('voip/startNewCall', {
        dataNewCall: {
          vehicleId: this.vehicle.vehicle_id,
          licensePlate: this.vehicle.license_plate,
          driver: this.vehicle.driver1?.fullname ?? null,
          imei: this.vehicle.imei,
          deviceType,
          isDebugBroadcast: false,
        },
      })
    },

    async handleToggleMute() {
      this.$store.commit('voip/toggleMuted')
      await this.$nextTick()
      this.$emit('handleToggleMic')
    },

    async handleStartCallDisconnect() {
      if (this.callState === 'active') {
        this.$emit('handleEndCall')
      } else {
        this.startCall()
      }
    },
  }
}
</script>


